import React, { useEffect, useState } from "react";
import { useRecoilValue, useRecoilState } from "recoil";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ThemeProvider } from "@mui/material/styles";
import { getFnolQuestionSetFromId } from "api-calls/GetExistingFnolQuestionSet";
import { fnolPageDeciderState } from "atoms/fnolPageDeciderState.atom";
import { schemeIdState } from "atoms/schemeIdState.atom";
import { fnolQuestionSetStructureState } from "atoms/fnolQuestionSetState.atom";
import { apiPrefixState } from "atoms/apiPrefixState.atom";
import { authenticationTokenState } from "atoms/authenticationTokenState.atom";
import { updateAuthenticationTokenState } from "store/UpdateAuthenticationTokenState";
import { updateUserState } from "store/UpdateUserState";
import PageDeciderViewer from "pages/PageDeciderViewer";
import * as constants from "constants/PageNameConstants";
import "App.css";
import Footer from "components/Footer";
import { generateTheme } from "theme";
import { Box, CircularProgress, Container, CssBaseline, Fade } from "@mui/material";
import { getCustomerTheme } from "api-calls/GetCustomerTheme";
import { useDispatch } from "react-redux";
import { setCustomerThemeData } from "slices/customerThemeSlice";

function App() {
  const apiPrefix = useRecoilValue(apiPrefixState);
  const authenticationToken = useRecoilValue(authenticationTokenState);

  const [, setFnolPage] = useRecoilState(fnolPageDeciderState);
  const [, setSchemeId] = useRecoilState(schemeIdState);
  const [customerTheme, setCustomerTheme] = useState(null);
  const dispatch = useDispatch();
  const fnolQuestionSet = useRecoilValue(fnolQuestionSetStructureState);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const response = await getCustomerTheme();
        const responseTheme = generateTheme(response?.theme || "");
        setCustomerTheme(responseTheme);

        // Handle favicon
        if (response?.favIcon && response.favIcon.trim() !== "") {
          try {
            const favicon = document.querySelector("link[rel='icon']") || document.createElement("link");
            favicon.type = "image/x-icon";
            favicon.rel = "icon";
            favicon.href = `data:image/png;base64,${response.favIcon}`;
            document.getElementsByTagName("head")[0].appendChild(favicon);
          } catch (error) {
            console.error("Error setting favicon:", error);
          }
        }

        // Dispatch theme data to Redux store
        dispatch(
          setCustomerThemeData({
            logo: response?.logo || "",
            decorativeImage: response?.decorativeImage || "",
            header: response?.header || "",
            subheader: response?.subheader || "",
            submissionSubText: response?.submissionSubText || "",
          })
        );
      } catch (error) {
        if (error && typeof error === "object") {
          console.error("Error in fetchTheme:", {
            message: error.message || "Unknown error",
            stack: error.stack,
            error: JSON.stringify(error),
          });
        } else {
          console.error("Error in fetchTheme:", error);
        }

        // Fallback to default theme if fetching fails
        const defaultTheme = generateTheme("");
        setCustomerTheme(defaultTheme);
      }
    };

    fetchTheme();
  }, []);

  // Update page title when fnol question set name changes
  useEffect(() => {
    if (fnolQuestionSet?.fnolQuestionSetName) {
      // Replace FNOL with ENOL in the name
      const titleName = fnolQuestionSet.fnolQuestionSetName.replace(/FNOL/gi, "ENOL");
      document.title = `${titleName}`;
    }
  }, [fnolQuestionSet?.fnolQuestionSetName]);

  useEffect(() => {
    // This is temporary until the proper proxy api is in place
    const devEnvironment = process.env.REACT_APP_ENV === "development";
    if (devEnvironment) {
      const tempAuthToken = process.env.REACT_APP_AUTHTOKEN;
      updateAuthenticationTokenState(tempAuthToken);
    }

    getFnolQuestionSetFromId(); // The proxy takes care of the fnolquestionsetId
    updateUserState(process.env.REACT_APP_USERID);
    setSchemeId(process.env.REACT_APP_SCHEMEID ?? "");

    setFnolPage({ stage: constants.TAKINGFNOL_PAGE });
  }, [apiPrefix, authenticationToken]);

  if (!customerTheme) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <ThemeProvider theme={customerTheme}>
        <CssBaseline />
        <Fade in={true} timeout={4000}>
          <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
            <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 4, sm: 8 } }}>
              <PageDeciderViewer />
            </Container>
            <Footer />
          </Box>
        </Fade>
      </ThemeProvider>
    </DndProvider>
  );
}

export default App;
